import { mutate } from 'swr';
import { deleteAPI } from '~/lib/api';
import { useUser } from './useUser';
import { NestedHome } from '@habity/types';
import { useTracking } from './useTracking';
import { useAgency } from './useAgency';
import { EVENTS } from '@habity/constants';
import { revalidateBuyerDashboard } from '~/app/actions/revalidateBuyerDashboard';
import { Icon } from '@chakra-ui/react';
import { useToast } from '~/lib/toasts';
import { TbHeartFilled } from 'react-icons/tb';
import { useFilters } from './useFilters';
import { useCallback } from 'react';
import { useUserAction } from './useUserAction';

export const useWishlist = () => {
  const { user } = useUser();
  const { getSearchRoute } = useFilters();
  const { track } = useTracking();
  const { agency } = useAgency();
  const { showToast } = useToast();

  // Use centralized action hook
  const { executeAction, isLoading: isAddingToWishlist } = useUserAction();

  /**
   * Add home to wishlist using unified flow via useUserAction:
   * - Logged-in users with complete profiles: action performed immediately, confirmation screen shown
   * - Logged-out users OR incomplete profiles: login modal shown at appropriate step
   */
  const addToWishlist = useCallback(
    async (home: NestedHome) => {
      const result = await executeAction({
        home,
        action: 'added_to_wishlist',
        redirectTo: getSearchRoute(home.id.toString()),
      });

      if (!result.success && result.error) {
        showToast(
          {
            title: 'Något gick fel',
            description: result.error,
          },
          { status: 'error' }
        );
      }
    },
    [executeAction, getSearchRoute, showToast]
  );

  const removeFromWishlist = async (home: NestedHome) => {
    if (!user) return;

    // Delete the added_to_wishlist interest record via the backend
    await deleteAPI(
      `/providers/supabase/interests/${user.id}/${home.id}/added_to_wishlist`
    );

    // Track wishlist remove event
    track(EVENTS.REMOVED_FROM_WISHLIST, {
      homeId: home.id,
      userId: user.id,
      agencyId: agency?.id,
      homeAddress: home.address?.street,
      homeCity: home.address?.city,
      homeZip: home.address?.zip,
      homeStatus: home.status,
    });

    // Trigger SWR revalidation to refresh user data
    mutate(['users', user?.id]);
    revalidateBuyerDashboard();

    // Show toast message with automatic deduplication
    showToast(
      {
        title: 'Borttagen från favoriter',
        description: `${home?.address?.street} har tagits bort från dina favoriter.`,
      },
      {
        icon: <Icon as={TbHeartFilled} boxSize={6} />,
      }
    );
  };

  return { addToWishlist, removeFromWishlist, isAddingToWishlist };
};
